import TemplateHelper from '../modules/template-helper';
import delegateListener from '../modules/delegate-listener';
import flatpickr from 'flatpickr';
import { Finnish } from 'flatpickr/dist/l10n/fi';

import SVGIcons from '../modules/svgicons';
import { triggerEvent } from '../modules/dom-helpers';

import { flatpickrSettings, flatpickrDatePattern, ISODatePattern } from '../modules/date-inputs';

const svgIcons = new SVGIcons();
const iconNext = svgIcons('caret-right');
const iconPrev = svgIcons('caret-left');

class ExpenseModal {
  constructor(options = {}) {
    const defaults = {
      openersParent: '.invoice-verification__table tbody',
      openerSelector: '[data-modal-target^="expense--"]',
      enabledPages: ['.page--expense-list'],
      modalTemplate: 'expense-modal',
      templateHelper: new TemplateHelper(),
      chargeTypesWithProvision: [],
      newExpenseForm: '[data-action="new-expense"]',
      triggerStatusOverrides: '[name="charge_type"]',
      expenseStatusSelect: '[name="expense_status"]',
      validationPatterns: {
        invoice_date: ISODatePattern,
        invoice_date_flatpickr: flatpickrDatePattern,
        received_date: ISODatePattern,
        received_date_flatpickr: flatpickrDatePattern
      }
    }

    const viewDefaults = {
      form_action: 'edit-expense',
      expense_type: 'other',
      modal_title: '',
      invoice_number: '',
      received_date: '',
      invoice_date: '',
      biller: '',
      biller_other: '',
      charge_type: '',
      amount_vatless: '',
      provision: '',
      vat: '',
      amount: '',
      description: '',
      currentModal: false,
      list_price: '',
      discount_percentage: ''
    };

    Object.assign(this, defaults, options);

    this._viewSettings = Object.assign({}, viewDefaults, options.viewSettings || {});

    // defaults for new expenses
    this.newExpense = { can_edit: '1' };
  }

  /**
   * Attaches events if currently on an enabled page
   */
  init() {
    if(document.querySelectorAll(this.enabledPages.join(',')).length <= 0) {
      // short circuit initialization, as we're on a page that doesn't need this module.
      return;
    }

    this.chargeTypesWithProvision = Object.values(
      window.EXPENSE_CHARGE_TYPES || []
    ).flat().filter(chargeType => {
      return chargeType.use_provision === '1'
    }).map(chargeType => chargeType.product_id);

    document.querySelectorAll('[name="amount"],[name="amount_vatless"],[name="vat"],[name="charge_type"],[name="provision"]').forEach(input => {
      input.addEventListener('change', this._handleAmountInputsChange(this.newExpense).bind(this));
    });

    document.querySelectorAll(this.newExpenseForm).forEach(form => {
      form.addEventListener('submit', function(e) {
        this.querySelector('button[type="submit"]').disabled = true;
      });
    });

    // This should run before ModalHelpers' delegated click handler
    document.body.addEventListener('click',
      delegateListener(this.openerSelector, this.handleModalOpenerClick.bind(this))
    );

    this.loadTemplateData(window.ITEM_ID);

    document.body.addEventListener('change',
      delegateListener(this.triggerStatusOverrides, this.handleStatusOverrideChange.bind(this))
    );
  }

  /**
   * Loads an item's expenses.
   *
   * TODO: Currently relies on window.EXPENSES, do we need an AJAX-version?
   *
   * @param {Number} itemId - The target item's pid (TODO: not implemented)
   */
  loadTemplateData(itemId) {
    if(typeof window.EXPENSES !== 'undefined') {
      this._expenses = window.EXPENSES;
    }
    if(typeof window.EXPENSE_TRANSLATIONS !== 'undefined') {
      this._translations = window.EXPENSE_TRANSLATIONS;
    }
    if(typeof window.WAREHOUSES !== 'undefined') {
      this._warehouses = window.WAREHOUSES;
    }
    if(typeof window.EXPENSE_CHARGE_TYPES !== 'undefined') {
      this._charge_types = window.EXPENSE_CHARGE_TYPES;
    }
    if(typeof window.CHARGE_TYPE_PRICES !== 'undefined') {
      this._charge_type_prices = window.CHARGE_TYPE_PRICES;
    }
  }

  /**
   * Finds the target expense's pid and ensures a corresponding modal exists in DOM.
   *
   * @param {Event} event - The handled event
   */
  handleModalOpenerClick(event) {
    const opener = event.delegateTarget || event.currentTarget;
    let modalTarget = opener.dataset.modalTarget;
    let expenseId = parseInt(modalTarget.match(/\d+/g)[0]);
    this.openModal(expenseId);
  }

  /**
   * Disables/enables an invoice status option based on the selected charge type
   *
   * @param {Event} event - The delegate event target
   * @param {String} [status] - The status value to disable/enable
   */
  handleStatusOverrideChange(event, status = 'will_bill') {
    const select = event.delegateTarget;
    const form = select.closest(this.newExpenseForm);
    if(!form) return; // Ignore if editing expense, rather than creating a new one
    const statusSelect = form.querySelector(this.expenseStatusSelect);
    const willBillOption = Array.from(statusSelect.options)
      .find(option => option.value === status);
    const selectedChargeType = this.getChargeTypeData(select.value);

    const shouldDisable = selectedChargeType[`disable_${status}`] === '1';
    willBillOption.disabled = shouldDisable;
    if(shouldDisable) statusSelect.selectedIndex = 0;
  }

  /**
   * Retrieves charge type data by name
   * @param {string} chargeType - The charge type name
   * @returns {Object|null}
   */
  getChargeTypeData(chargeType) {
    return Object.values(this._charge_types).flat().find(type => type.product_id === chargeType);
  }

  /**
   * Retreives a single expense's data.
   *
   * @param {Number} expenseId - The target expense's pid
   *
   * @returns {Object} - The expense data
   */
  getExpense(expenseId) {
    const result = this._expenses.filter(expense => expense.pid === expenseId);
    return result.length > 0 ? result[0] : {};
  }

  /**
   * Opens a given expense's modal. Creates it if necessary.
   *
   * @param {Number} expenseId - The target expense's pid
   * @returns {HTMLElement} - The target expense's modal
   */
  openModal(expenseId) {
    if(typeof this._modals === 'undefined') {
      this._modals = {};
    }
    if(typeof this._modals[expenseId] === 'undefined') {
      return this._modals[expenseId] = this.createExpenseModal(expenseId);
    } else {
      return this._modals[expenseId];
    }
  }

  /**
   * Creates and inserts an edit media modal into the DOM
   *
   * @param {int} expenseId - Expense id
   *
   * @returns {HTMLElement} - The generated modal element
   */
  createExpenseModal(expenseId) {
    const expense = this.getExpense(expenseId);
    const view = this.formatView(expense);

    const element = this.templateHelper.render(
      this.modalTemplate,
      view,
      document.body,
      'append',
      ['text_input', 'date_input', 'single_select', 'textarea']
    );

    element.querySelectorAll('[type="date"]').forEach(dateInput => {
      flatpickr(dateInput, flatpickrSettings);
    });

    // Bind expense amount calculator events
    element.querySelectorAll(
      '[name="amount"],[name="amount_vatless"],[name="vat"],[name="charge_type"],[name="provision"]'
    ).forEach(input => {
      input.addEventListener('change', this._handleAmountInputsChange(expense).bind(this));
    });
    this.togglePricesReadOnly(element, expense);

    return element;
  }

  /**
   * Creates an event handler with the relevant expense in it's scope
   *
   * @param {Object} expense - The expense object
   * @returns {function} - The event handler
   */
  _handleAmountInputsChange(expense) {
    /**
     * Handles 'change' event for inputs that relate to calculating expense amounts.
     *
     * @param {Object} event - The event object
     */
    return function (event) {
      const form = event.currentTarget.closest('form');

      // store a reference to relevant inputs
      let [ totalEl, amountEl, vatEl, provisionEl, chargeTypeEl ] = [
        'amount', 'amount_vatless', 'vat', 'provision', 'charge_type'
      ].map(name => form.querySelector(`[name="${ name }"]`));

      // Mock provision element, if it's not present on the form
      if(!provisionEl) provisionEl = { value: 0 };

      switch(event.currentTarget.name) {
        case 'charge_type': {
          // Queue a change event for amount field
          const showProvision = this.chargeTypesWithProvision.includes(chargeTypeEl.value);
          this.toggleProvisionField(provisionEl, showProvision);
          //this.togglePricesReadOnly(form, { ...expense, charge_type: chargeTypeEl.value });
          setTimeout(()=>{
            triggerEvent(amountEl, 'change');
            this.togglePricesReadOnly(form, { ...expense, charge_type: chargeTypeEl.value });
          }, 5);
          break;
        }
        case 'amount':
          amountEl.value = this.calculateAmount(
            parseFloat(totalEl.value, 10),
            parseFloat(vatEl.value, 10) / 100,
            parseFloat(provisionEl.value, 10) / 100
          ).toFixed(2);
          break;
        case 'provision':
        case 'amount_vatless':
        case 'vat':
          totalEl.value = this.calculateTotal(
            parseFloat(amountEl.value, 10),
            parseFloat(vatEl.value, 10) / 100,
            parseFloat(provisionEl.value, 10) / 100
          ).toFixed(2);
          break;
      }
    }
  }

  calculateTotal(amount, vat, provision) {
    // SDSTA-4234: to correct rounding errors in the 3rd decimal place and beyond,
    // we apply a correction factor (cf) to each variable to bring them to
    // integer space, convert the variables to integers, perform the calculation,
    // and return the result as a float with the correction factors removed.
    
    // Constant factor to increase precision by converting decimal numbers into integers
    const cf = 1000;

    // Multiply the amount by cf (1000) to convert it to an integer and increase precision, then round it
    const amountPrecise = Math.round(amount * cf);

    // Calculate the precise VAT factor (1 + vat), multiply by cf (1000) to make it an integer, then round it
    // For example, if VAT is 0.255 (25,5%), 1 + vat becomes 1.255, and it's converted to a precise integer
    const vatPrecise = Math.round((1 + vat) * cf);

    // Similarly, calculate the precise provision factor (1 + provision), multiply by cf (1000), and round it
    // For example, if provision is 0.10 (10%), 1 + provision becomes 1.10 and is converted to a precise integer
    const provisionPrecise = Math.round((1 + provision) * cf);

    // Multiply the three precise values (amount, VAT, and provision), then divide by cf^3 to adjust the scale
    // This division brings the result back from the high precision (integers) to the original scale
    const totalPrecise = (amountPrecise * vatPrecise * provisionPrecise) / (cf ** 3);

    // Round the final result to 2 decimal places (e.g., for currency format) and return the value
    return Math.round(totalPrecise * 100) / 100;
  }

  calculateAmount(total, vat, provision) {
    return total / (1 + provision) / (1 + vat);
  }

  /**
   * Shows or hides the provision field in expense modals.
   * @param {HTMLInputElement} input - The current provision input element
   * @param {Boolean} visible - Whether the provision field should be shown or not
   */
  toggleProvisionField(input, visible = true) {
    if(!(input instanceof HTMLElement)) { // Exit if input isn't an input…
      return;
    }
    const form = input.closest('form');
    const provisionContainer = form.querySelector('.js-provision');
    const vatContainer = form.querySelector('.js-vat');

    provisionContainer.classList.toggle('hidden', !visible);
    provisionContainer.classList.toggle('col-xs-12', visible);
    provisionContainer.classList.toggle('col-sm-3', visible);
    vatContainer.classList.toggle('col-sm-6', !visible);
    vatContainer.classList.toggle('col-sm-3', visible);

    // We also want to set the provision field's value to 0 while
    // it's hidden, to ensure prices are calculated correctly.
    if(visible) {
      if(this._currentClientProvision) {
        input.value = this._currentClientProvision;
      }
    } else if(input.value && input.value !== '0') {
      this._currentClientProvision = input.value;
      input.value = 0;
    }
  }

  /**
   * Marks price inputs as `readonly`, when expense editing is disallowed
   * or when the selected charge type doesn't use a separate pricing table.
   * When neither of these conditions are filled, the input is re-enabled.
   *
   * @param {HTMLFormElement} form - The associated form
   * @param {Object} expense - The associated expense data
   */
  togglePricesReadOnly(form, expense) {
    const chargeType = form.querySelector('[name="charge_type"]').value;
    const chargeTypeData = this.getChargeTypeData(chargeType);
    const isPriceEditingEnabled = !chargeTypeData || chargeTypeData.separate_price_list === '1';
    form.querySelectorAll(
      '[name="amount"],[name="amount_vatless"],[name="vat"],[name="provision"]'
    ).forEach(input => {
      input.readOnly = !expense.can_edit || !isPriceEditingEnabled;
    });
  }

  /**
   * Formats expense data for template
   *
   * @param {Object} expense - The expense data
   * @returns {Object} - The view object for mustache
   */
  formatView(expense) {
    let view = {
      modal_name: `expense--${expense.pid}`,
      modal_title: this._translations.modal_title_edit,
      form_action: 'edit-expense',
      expense_status: this.selectExpenseStatus(window.EXPENSE_STATUSES, expense),
      edit_pid: true,
      item: window.ITEM_ID,
      attachment: false,
      show_provision: this.chargeTypesWithProvision.includes(expense.charge_type),
      expense_type: expense.expense_type,
      pid: expense.pid,
      discount_percentage: expense.discount_percentage,
      list_price: expense.list_price
    };

    view.description = this.textareaView('description', expense);
    view['received_date'] = this.dateView('received_date', expense);
    view['invoice_date'] = this.dateView('invoice_date', expense, true);
    ['invoice_number', 'biller_other'].forEach(k=>{
      view[k] = this.textView(k, expense)
    }, this);
    ['amount_vatless', 'vat', 'amount', 'provision'].forEach(k=>{
      view[k] = this.numberView(k, expense, 'any')
    }, this);

    if(view.show_provision) {
      this.calculateProvision(expense);
      view.provision = this.numberView('provision', {
        provision: this.calculateProvision(expense)
      }, 'any');
    }

    view.biller = this.selectExpenseBiller(this._warehouses, expense);

    if(window.EXPENSE_CHARGE_TYPES.hasOwnProperty(expense.expense_type)) {
      view.charge_type = this.selectChargeTypeView(
        expense,
        window.EXPENSE_CHARGE_TYPES[expense.expense_type].map(chargeType => {
          return chargeType.product_id
        })
      );
    }

    view.attachment = expense.attachment;

    //Add linear navigation to expense view.
    const pids = window.EXPENSES.map(expense => expense.pid);
    const index = pids.indexOf(expense.pid);
    view.next = pids[index + 1] || false;
    view.prev = pids[index - 1] || false;

    return view;
  }

  /**
   * Calculates the provision percentage
   * @param {Object} expense - The expense data
   * @param {String|Number} expense.amount - Price incl. provision and VAT
   * @param {String|Number} expense.amount_vatless - Price incl. provision
   * @param {String|Number} expense.vat - Vat percentage (0-100)
   * @returns {String} - Provision percentage (0-100)
   */
  calculateProvision(expense) {
    const { amount, amount_vatless, vat } = expense;
    const amountWithoutVat = amount / (1 + vat/100);
    const provisionPercentage = (amountWithoutVat / amount_vatless) - 1;
    return Math.round(provisionPercentage * 10000) / 100;
  }

  selectExpenseBiller(warehouses, expense) {
    if(expense.expense_type !== 'realization') {
      return this.selectView('biller', expense, ['SVT']);
    }

    return {
      selectName: 'biller',
      title: this._translations.biller,
      options: [
        { value: '0', title: this._translations.select_placeholder, disabled: true, selected: !expense.biller || expense.biller === '0' },
        ...warehouses.map(warehouse => {
          return {
            value: warehouse.pid,
            title: warehouse.name,
            selected: parseInt(expense.biller) === warehouse.pid,
          };
        })
      ],
      disabled: !expense.can_edit
    }
  }


  /**
   * Format view for expense_status
   *
   * @param {Array} statuses - List of all statuses
   * @param {Object} expense - Current expense
   * @returns {Object} - View object that can be passed on to Mustache
   */
  selectExpenseStatus(statuses, expense) {
    return {
      selectName: 'expense_status',
      title: this._translations.expense_status,
      options: [
        {
          value: '0',
          title: this._translations.select_placeholder,
          disabled: true,
          selected: !expense.expense_status
        },
        ...statuses.map(status => {
          let disabled = true;
          if(
            expense.form_action === 'new-expense' &&
            status.enabledForNewExpense
          ) {
            disabled = false;
          } else { // editing existing expense
            if(
              status.enabledForStatuses &&
              [...status.enabledForStatuses].includes(expense.expense_status)
            ) {
              disabled = false;
            }
          }
          return {
            value: status.value,
            title: this._translations['expense_status__' + status.value],
            selected: expense.expense_status === status.value,
            disabled: disabled
          };
        })
      ]
    }
  }

  selectChargeTypeView(expense, opts) {
    const view = this.selectView('charge_type', expense, opts);
    const modalName = 'expense--' + expense.pid;
    view.dataAttributes = [{
      name: 'prefill-data',
      value: JSON.stringify(this._charge_type_prices)
    }, {
      name: 'prefill-target',
      value: JSON.stringify({
        price: `[data-modal="${ modalName }"] [name="amount_vatless"]`,
        vat: `[data-modal="${ modalName }"] [name="vat"]`,
        list_price: `[data-modal="${modalName }"] [name="list_price"]`,
        discount_percentage: `[data-modal="${ modalName }"] [name="discount_percentage"]`
      })
    }];
    return view;
  }

  /**
   * Format view for <select> elements
   * @param {string} key - The field name
   * @param {Object} expense - The current expense
   * @param {*} opts -
   * @returns {Object} - View object that can be passed on to Mustache
   */
  selectView(key, expense, opts = []) {
    let placeholder = {
      value: '0',
      title: this._translations.select_placeholder,
      disabled: true,
      selected: !expense[key]
    };
    return {
      selectName: key,
      title: this._translations[key],
      options: [placeholder, ...opts.map(opt => {
        const selected = expense[key] === opt;
        return {
          value: opt,
          title: this._translations[key + '__' + opt],
          selected
        };
      })],
      disabled: !expense.can_edit
    };
  }

  dateView(key, expense, required = false) {
    return {
      dateName: key,
      date: expense[key],
      readOnly: !expense.can_edit,
      disabled: !expense.can_edit, // flatpickr doesn't seem to support readonly attr
      title: this._translations[key],
      pattern: this.validationPatterns[key],
      required
    };
  }

  numberView(key, expense, step, max, min) {
    return {
      readOnly: !expense.can_edit,
      textName: key,
      textValue: parseFloat(expense[key]),
      title: this._translations[key],
      type: 'number',
      min, max, step
    };
  }

  textView(key, expense) {
    return {
      readOnly: !expense.can_edit,
      textName: key,
      textValue: expense[key],
      title: this._translations[key],
      type: 'text'
    };
  }

  textareaView(key, expense) {
    return {
      textareaName: key,
      textareaValue: expense[key],
      title: this._translations[key]
    };
  }

}

module.exports = ExpenseModal;
